import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['modal', 'formSection', 'modalTitle', 'successModal', 'successDetails']
  static values = {
    opportunityId: String,
    payNowTitle: String
  }

  connect() {
    this.captureOverlayElements()
    this.hoistOverlaysToBody()
    this.checkPastDuePayments()
    this.boundCardAddedHandler = this.handleCardAdded.bind(this)
  }

  disconnect() {
    this.removeFormListener('credit-card-form:card-added', this.boundCardAddedHandler)
    this.returnOverlaysToPage()
  }

  // Stimulus resolves targets inside the controller element, so capture them before
  // hoistOverlaysToBody moves them out of it
  captureOverlayElements() {
    this.modalEl = this.hasModalTarget ? this.modalTarget : null
    this.successModalEl = this.hasSuccessModalTarget ? this.successModalTarget : null
    this.formSectionEl = this.hasFormSectionTarget ? this.formSectionTarget : null
    this.modalTitleEl = this.hasModalTitleTarget ? this.modalTitleTarget : null
    this.successDetailsEl = this.hasSuccessDetailsTarget ? this.successDetailsTarget : null
  }

  // Some themes style `.separator` (also a portal layout class) with a transform, which makes
  // that ancestor the overlay's stacking context — trapping its z-index below the page backdrop
  hoistOverlaysToBody() {
    this.hoistedOverlays = []

    for (const overlay of [this.modalEl, this.successModalEl]) {
      if (!overlay || overlay.parentElement === document.body) { continue }

      this.hoistedOverlays.push({
        overlay: overlay,
        parent: overlay.parentElement,
        nextSibling: overlay.nextSibling
      })

      overlay.classList.add('customer-portal')
      document.body.appendChild(overlay)
      this.bindActionsOutsideScope(overlay)
    }
  }

  // Once hoisted, Stimulus can't resolve a scope for the overlay's data-action attributes
  // (no ancestor with data-controller="payment-terms"), so rewire them by hand
  bindActionsOutsideScope(overlay) {
    overlay.querySelectorAll('[data-action]').forEach((el) => {
      el.dataset.action.split(' ').forEach((descriptor) => {
        const match = descriptor.match(/^(\w+)->payment-terms#(\w+)$/)
        if (!match) return

        const [, eventName, methodName] = match
        el.addEventListener(eventName, (event) => this[methodName](event))
      })
    })
  }

  returnOverlaysToPage() {
    (this.hoistedOverlays || []).forEach(({ overlay, parent, nextSibling }) => {
      overlay.classList.remove('customer-portal')
      parent.insertBefore(overlay, nextSibling)
    })

    this.hoistedOverlays = []
  }

  addFormListener(name, handler) {
    this.formSectionEl?.addEventListener(name, handler)
  }

  removeFormListener(name, handler) {
    this.formSectionEl?.removeEventListener(name, handler)
  }

  getLocale() {
    return document.documentElement.lang || 'en'
  }

  showModal() {
    if (!this.modalEl) return

    this.modalEl.classList.add('customer-portal-modal', 'show')
    this.modalEl.setAttribute('aria-modal', 'true')
    this.modalEl.removeAttribute('aria-hidden')

    if (!document.querySelector('.customer-portal-modal-backdrop[data-backdrop-for="addCardModal"]')) {
      const backdrop = document.createElement('div')
      backdrop.className = 'customer-portal-modal-backdrop modal-backdrop fade show'
      backdrop.dataset.backdropFor = 'addCardModal'
      backdrop.addEventListener('click', () => this.closeModal())
      document.body.appendChild(backdrop)
    }
    document.body.classList.add('modal-open')
  }

  openModal() {
    this.showModal()
    this.loadCardForm()
  }

  loadCardForm() {
    if (!this.formSectionEl) return

    const locale = this.getLocale()
    const url = `/${locale}/oa/opportunities/${this.opportunityIdValue}/payment_terms/add_card_form`

    fetch(url, {
      headers: {
        'Accept': 'text/html'
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        return response.text()
      })
      .then(html => {
        this.formSectionEl.innerHTML = html

        // Show inline form if it's hidden (for original portal non-modal version)
        if (this.formSectionEl.style.display === 'none') {
          this.formSectionEl.style.display = 'block'
        }

        this.addFormListener('credit-card-form:card-added', this.boundCardAddedHandler)

        // Do NOT mount Stripe Elements again here — the injected form's own credit-card-form
        // controller already does, and a second mount breaks the form
      })
      .catch(error => {
        console.error('Error loading form:', error)
        this.formSectionEl.innerHTML = '<div class="alert alert-danger">Failed to load the form. Please try again.</div>'
      })
  }

  handleCardAdded(event) {
    const cardId = event.detail.id

    if (!cardId) {
      console.error('No card ID received from credit card form')
      return
    }

    this.loadPaymentTermsSelection(cardId)
  }

  loadPaymentTermsSelection(cardId) {
    if (!this.formSectionEl) return

    const locale = this.getLocale()
    const url = `/${locale}/oa/opportunities/${this.opportunityIdValue}/payment_terms/select_payment_terms?card_id=${cardId}`

    fetch(url, {
      headers: {
        'Accept': 'text/html'
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        return response.text()
      })
      .then(html => {
        this.formSectionEl.innerHTML = html

        this.boundTermsUpdatedHandler = this.handleTermsUpdated.bind(this)
        this.addFormListener('payment-term-selector:terms-updated', this.boundTermsUpdatedHandler)
      })
      .catch(error => {
        console.error('Error loading payment terms selection:', error)
        this.formSectionEl.innerHTML = '<div class="alert alert-danger">Failed to load payment terms. Please try again.</div>'
      })
  }

  handleTermsUpdated() {
    if (this.boundTermsUpdatedHandler) {
      this.removeFormListener('payment-term-selector:terms-updated', this.boundTermsUpdatedHandler)
    }

    this.closeModal()
    this.refreshPaymentTerms()
  }

  cleanupForm() {
    if (this.formSectionEl) {
      this.formSectionEl.innerHTML = ''
    }
    this.removeFormListener('credit-card-form:card-added', this.boundCardAddedHandler)

    if (this.boundTermsUpdatedHandler) {
      this.removeFormListener('payment-term-selector:terms-updated', this.boundTermsUpdatedHandler)
    }

    const backdrop = document.querySelector('.modal-backdrop[data-backdrop-for="addCardModal"]')
    if (backdrop) {
      backdrop.remove()
    }
    document.body.classList.remove('modal-open')
  }

  refreshPaymentTerms() {
    // Timestamp param busts any cache on the hard reload
    const url = new URL(window.location.href)
    url.searchParams.set('_t', Date.now())
    window.location.href = url.toString()
  }

  payNow(event) {
    const paymentTermId = event.detail.paymentTermId

    if (!paymentTermId) {
      console.error('No payment term ID received')
      return
    }

    this.openPayNowModal(paymentTermId)
  }

  openPayNowModal(paymentTermId) {
    if (!this.modalEl) {
      console.error('Modal target not found')
      return
    }

    if (this.modalTitleEl) {
      this.modalTitleEl.textContent = this.payNowTitleValue
    }

    this.paymentTermId = paymentTermId
    this.showModal()
    this.loadPayNowForm(paymentTermId)
  }

  closeModal() {
    if (this.modalEl) {
      this.modalEl.classList.remove('show')
      this.modalEl.setAttribute('aria-hidden', 'true')
      this.modalEl.removeAttribute('aria-modal')

      const backdrop = document.querySelector('.customer-portal-modal-backdrop[data-backdrop-for="addCardModal"]')
      if (backdrop) {
        backdrop.remove()
      }
      document.body.classList.remove('modal-open')
    } else if (this.formSectionEl) {
      // For original portal: hide inline form section
      this.formSectionEl.style.display = 'none'
    }

    this.cleanupForm()
  }

  loadPayNowForm(paymentTermId) {
    if (!this.formSectionEl) return

    const locale = this.getLocale()
    const url = `/${locale}/oa/opportunities/${this.opportunityIdValue}/payment_terms/pay_now_form?payment_term_id=${paymentTermId}`

    fetch(url, {
      headers: {
        'Accept': 'text/html'
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        return response.text()
      })
      .then(html => {
        this.formSectionEl.innerHTML = html

        this.boundPaymentCompletedHandler = this.handlePaymentCompleted.bind(this)
        this.addFormListener('pay-now-form:payment-completed', this.boundPaymentCompletedHandler)

        // Stripe Elements are mounted by the injected credit-card-form controller; don't double-mount
      })
      .catch(error => {
        console.error('Error loading pay now form:', error)
        this.formSectionEl.innerHTML = '<div class="alert alert-danger">Failed to load payment form. Please try again.</div>'
      })
  }

  handlePaymentCompleted(event) {
    if (!this.formSectionEl) return

    const paymentTermId = event?.detail?.paymentTermId
    this.lastPaidPaymentTermId = paymentTermId

    if (this.boundPaymentCompletedHandler) {
      this.removeFormListener('pay-now-form:payment-completed', this.boundPaymentCompletedHandler)
    }

    this.closeModal()

    setTimeout(() => {
      this.showSuccessModal()
    }, 300)
  }

  showSuccessModal() {
    if (!this.successModalEl) return

    this.fetchPaymentSummary()
      .then(() => {
        this.successModalEl.classList.add('customer-portal-modal', 'show')
        this.successModalEl.setAttribute('aria-modal', 'true')
        this.successModalEl.removeAttribute('aria-hidden')

        if (!document.querySelector('.customer-portal-modal-backdrop[data-backdrop-for="paymentSuccessModal"]')) {
          const backdrop = document.createElement('div')
          backdrop.className = 'customer-portal-modal-backdrop modal-backdrop fade show'
          backdrop.dataset.backdropFor = 'paymentSuccessModal'
          backdrop.addEventListener('click', () => {
            this.closeSuccessModal()
          })
          document.body.appendChild(backdrop)
        }
        document.body.classList.add('modal-open')
      })
      .catch(error => {
        console.error('Error showing success modal:', error)
        this.refreshPaymentTerms()
      })
  }

  closeSuccessModal() {
    if (!this.successModalEl) return

    this.successModalEl.classList.remove('show')
    this.successModalEl.setAttribute('aria-hidden', 'true')
    this.successModalEl.removeAttribute('aria-modal')

    const backdrop = document.querySelector('.customer-portal-modal-backdrop[data-backdrop-for="paymentSuccessModal"]')
    if (backdrop) {
      backdrop.remove()
    }
    document.body.classList.remove('modal-open')

    // Delay to let the backend finish processing the payment before refreshing
    setTimeout(() => {
      this.refreshPaymentTerms()
    }, 1500)
  }

  async fetchPaymentSummary() {
    if (!this.successDetailsEl) return

    const locale = this.getLocale()
    let url = `/${locale}/oa/opportunities/${this.opportunityIdValue}/payment_terms/payment_summary`

    if (this.lastPaidPaymentTermId) {
      url += `?payment_term_id=${this.lastPaidPaymentTermId}`
    }

    try {
      const response = await fetch(url, {
        headers: {
          'Accept': 'text/html'
        }
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error('Payment summary error response:', errorText)
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const html = await response.text()

      if (html.trim().length === 0) {
        console.warn('Empty HTML received from payment summary')
        this.successDetailsEl.innerHTML = '<p class="text-muted">Payment successful!</p>'
      } else {
        this.successDetailsEl.innerHTML = html
      }
    } catch (error) {
      console.error('Error fetching payment summary:', error)
      this.successDetailsEl.innerHTML = '<p class="text-muted">Payment successful!</p>'
    }
  }

  checkPastDuePayments() {
    const paymentDetailsDiv = document.querySelector('.opportunity-payment-terms')
    if (!paymentDetailsDiv) return

    const chargeableTerms = this.element.querySelectorAll('[data-controller*="chargeable-payment-term"]')
    let hasPastDue = false

    chargeableTerms.forEach((term, index) => {
      const dueDateValue = term.dataset.chargeablePaymentTermDueDateValue
      const dueDate = new Date(dueDateValue)
      const now = new Date()

      if (dueDate <= now && index === 0) {
        hasPastDue = true
      }
    })

    if (hasPastDue) {
      paymentDetailsDiv.classList.add('past-due')
      const header = paymentDetailsDiv.querySelector('.opportunity-details-header')
      const content = paymentDetailsDiv.querySelector('.opportunity-details-content')
      if (header && content) {
        header.classList.remove('closed')
        header.classList.add('opened')
        content.classList.remove('closed')
        content.classList.add('opened')
      }
    }
  }
}
